import { forwardRef, useMemo } from 'react'
import type { Object } from 'ts-toolbelt'
import { CircularProgress } from '@mui/material'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import UpsellHighIcon from '@mui/icons-material/AutoFixHigh'
import { NoWrapMessageContent } from './components/NoWrapMessageContent/NoWrapMessageContent'
import * as $ from './MessageCard.styled'

export interface MessageCardProps extends Object.Required<$.MessageCardProps, 'slots'> {
  noWrap?: boolean
}

/**
 * A `Card` that reads as a message: `variant` paints it by severity and supplies the matching leading
 * icon, which `slots.left` still overrides. Spans its container unless `fullWidth` says otherwise.
 *
 * The default choice for banners, notices and inline warnings — drop to `Card` only for a shell that
 * carries no severity.
 */
export const MessageCard = forwardRef<HTMLDivElement, MessageCardProps>(
  ({ slots, children, variant, fullWidth = true, noWrap = false, ...props }, ref) => {
    const icon = useMemo(() => {
      switch (variant) {
        case 'warning':
        case 'error':
          return <ErrorOutlineOutlinedIcon />
        case 'success':
          return <CheckCircleOutlinedIcon />
        case 'upsell':
          return <UpsellHighIcon />
        case 'loading':
          return <CircularProgress size={18} color="inherit" />
        default:
          return <InfoOutlinedIcon sx={{ color: ({ tokens }) => tokens.ThemeColorCommonOnSurfaceTertiary }} />
      }
    }, [variant])

    return (
      <$.MessageCard
        ref={ref}
        {...props}
        fullWidth={fullWidth}
        variant={variant}
        // Severity in the DOM, so a container can lay cards out by it. Kept after the spread so it
        // cannot disagree with the `variant` that paints the card.
        data-variant={variant ?? 'default'}
        slots={{
          ...slots,
          left: slots?.left ?? icon,
          content: noWrap ? <NoWrapMessageContent>{slots.content}</NoWrapMessageContent> : slots.content,
        }}
      >
        {children}
      </$.MessageCard>
    )
  },
)

MessageCard.displayName = 'MessageCard'
